import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { ColumnState, RowNode } from 'ag-grid-community';
import { RespondentRequestEntity, useCalendarApiEntity } from 'src/api/useCalendarApi';
import { UserEntity } from 'src/api/useUserApi';
import { CALENDAR_EDIT_TAB_VALUE, TabValue } from '../constants';
import { CalendarInfoEntity, initCalendarData } from '../hooks/useCalendarLayout';
import { ReminderEntity } from '../hooks/useReminderListGrid';

export interface SelectedCalendarListEntity {
  [rowId: string]: useCalendarApiEntity;
}
export interface SearchCalendarEntity {
  surveyId: string;
  year: string;
  month: string;
  day: string;
}

export const enqueteCalendarSlice = createSlice({
  name: 'enqueteCalendar',
  initialState: {
    // ローディングフラグ
    isLoading: false,
    storageKey: 'EnqueteCalendarListGrid' as string,
    searchCalendar: [] as SearchCalendarEntity[],
    enqueteCalendarlimitedlistData: [] as useCalendarApiEntity[],
    // 選択されているカレンダー
    tempSelectedCalendarList: {} as SelectedCalendarListEntity,
    // 選択されているカレンダーの件数
    tempSelectedCalendarCount: 0,
    // 明細カレンダーデータ
    surveyListData: [] as RespondentRequestEntity[],
    // 非活性フラグ
    isDisabled: false,
    // ダイアログオープンフラグ
    isOpen: false,
    // アンケート詳細Idリスト
    surveyIds: [] as string[],
    // 検索条件：年
    year: '' as string,
    // 検索条件：月
    month: '' as string,
    // 検索条件：日
    day: '' as string,
    // ページ
    page: 1 as number,
    blockSize: 100 as number,
    maxRow: 1 as number,
    startRowPosition: 0 as number,
    startPagePosition: 1 as number,
    useSortModel: [] as ColumnState[],
    isDestroyed: false,
    // 検索条件：月の非活性フラグ
    monthDisabled: true,
    // 検索条件：日の非活性フラグ
    dayDisabled: true,
    // カレンダーデータ
    calendarData: initCalendarData,
    // リマインド明細データ
    reminderListData: [] as ReminderEntity[],
    // カレンダー編集画面のタブの値
    tabValue: CALENDAR_EDIT_TAB_VALUE.JAPANESE as TabValue,
    // リマインド依頼ダイアログの明細データ
    rowData: [] as UserEntity[],
    // リマインド依頼ダイアログの追加ユーザ
    selectedUser: null as string | null,
    // 確認者
    isConfirmor: false,
    // 前回検索条件 アンケート
    lastSurveys: [] as string[],
    // 前回検索条件：年
    lastYear: '' as string,
    // 前回検索条件：月
    lastMonth: '' as string,
    // 前回検索条件：日
    lastDay: '' as string,
    // 検索条件クリアフラグ
    clearSearchFlag : true as boolean,
  },
  reducers: {
    // ローディングフラグを設定
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // 明細カレンダーデータを設定
    initSurveyListData: (state, action: PayloadAction<RespondentRequestEntity[]>) => {
      state.surveyListData = action.payload;
    },
    initEnqueteCalendarLimitedListData: (state, action: PayloadAction<useCalendarApiEntity[]>) => {
      state.tempSelectedCalendarList = {};
      state.tempSelectedCalendarCount = 0;
      state.enqueteCalendarlimitedlistData = action.payload;
    },
    // 選択したカレンダーデータを設定
    setTempSelectedCalendarList: (state, action: PayloadAction<RowNode[]>) => {
      const list: SelectedCalendarListEntity = {};
      action.payload.forEach((node: RowNode, i: number) => {
        if (node.isSelected()) list[node.rowIndex as number] = node.data;
      });
      state.tempSelectedCalendarList = list;
      state.tempSelectedCalendarCount = Object.keys(list).length;
    },
    // 選択したカレンダーデータ削除後、画面再表示
    deleteCalendarRowData: (state, action: PayloadAction<string[]>) => {
      const listData = current(state.enqueteCalendarlimitedlistData);
      state.tempSelectedCalendarList = {};
      state.enqueteCalendarlimitedlistData = listData.filter((rowData) => !action.payload.includes(rowData.id));
    },
    setSearchCalendar: (state, action: PayloadAction<SearchCalendarEntity[]>) => {
      state.searchCalendar = action.payload;
    },
    // 非活性フラグを設定
    setDisabled: (state, action: PayloadAction<boolean>) => {
      state.isDisabled = action.payload;
    },
    // ダイアログオープンフラグを設定
    setIsOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    // アンケート詳細Idリストを設定
    setSurveyIds: (state, action: PayloadAction<string[]>) => {
      const list: string[] = [];
      if (!Array.isArray(action.payload)) {
        list.push(action.payload);
      } else {
        action.payload.forEach((surveyId) => {
          list.push(surveyId);
        });
      }
      state.surveyIds = list;
    },
    // 検索条件：年を設定
    setYear: (state, action: PayloadAction<string>) => {
      state.year = action.payload;
    },
    // 検索条件：月を設定
    setMonth: (state, action: PayloadAction<string>) => {
      state.month = action.payload;
    },
    // 検索条件：日を設定
    setDay: (state, action: PayloadAction<string>) => {
      state.day = action.payload;
    },
    // ページを設定
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setBlockSize: (state, action: PayloadAction<number>) => {
      state.blockSize = action.payload;
    },
    setMaxRow: (state, action: PayloadAction<number>) => {
      state.maxRow = action.payload;
    },
    setUseSortModel: (state, action: PayloadAction<ColumnState[]>) => {
      state.useSortModel = action.payload;
    },
    setIsDestroyed: (state, action: PayloadAction<boolean>) => {
      state.isDestroyed = action.payload;
    },
    // 検索条件：月の非活性フラグを設定
    setMonthDisabled: (state, action: PayloadAction<boolean>) => {
      state.monthDisabled = action.payload;
    },
    // 検索条件：日の非活性フラグを設定
    setDayDisabled: (state, action: PayloadAction<boolean>) => {
      state.dayDisabled = action.payload;
    },
    /** calendarData start */
    // カレンダーデータを設定
    setCalendarData: (state, action: PayloadAction<CalendarInfoEntity>) => {
      state.calendarData = action.payload;
    },
    // カレンダーデータをクリア
    clearCalendarData: (state) => {
      state.calendarData = initCalendarData;
    },
    /** calendarData end */
    // リマインド明細データを設定
    setReminderListData: (state, action: PayloadAction<ReminderEntity[]>) => {
      state.reminderListData = action.payload;
    },
    // リマインド明細データをクリア
    clearReminderListData: (state) => {
      state.reminderListData = [];
    },
    // カレンダー編集画面のタブの値を設定
    setTabValue: (state, action: PayloadAction<TabValue>) => {
      state.tabValue = action.payload;
    },
    // リマインド依頼ダイアログの明細データを設定
    setRowData: (state, action: PayloadAction<Array<UserEntity>>) => {
      state.rowData = action.payload;
    },
    // リマインド依頼ダイアログの追加ユーザを設定
    setSelectedUser: (state, action: PayloadAction<string | null>) => {
      state.selectedUser = action.payload;
    },
    // 確認者を設定
    setIsConfirmor: (state, action: PayloadAction<boolean>) => {
      state.isConfirmor = action.payload;
    },
    // 前回検索条件 アンケートを設定
    setLastSurveys: (state, action: PayloadAction<string[]>) => {
      state.lastSurveys = action.payload;
    },
    // 前回検索条件：年を設定
    setLastYear: (state, action: PayloadAction<string>) => {
      state.lastYear = action.payload;
    },
    // 前回検索条件：月を設定
    setLastMonth: (state, action: PayloadAction<string>) => {
      state.lastMonth = action.payload;
    },
    // 前回検索条件：日を設定
    setLastDay: (state, action: PayloadAction<string>) => {
      state.lastDay = action.payload;
    },
    // 検索条件クリアフラグを設定
    setClearSearchFlag: (state, action: PayloadAction<boolean>) => {
      state.clearSearchFlag = action.payload;
    },
  },
});
export const {
  // ローディングフラグの設定
  setIsLoading,
  // 非活性フラグの設定
  setDisabled,
  // ダイアログオープンフラグの設定
  setIsOpen,
  // 選択したカレンダーデータの設定
  setTempSelectedCalendarList,
  // 選択したカレンダーデータ削除後、画面再表示
  deleteCalendarRowData,
  // アンケート詳細Idリストの設定
  setSurveyIds,
  // 検索条件：年を設定
  setYear,
  // 検索条件：月を設定
  setMonth,
  // 検索条件：日を設定
  setDay,
  // ページを設定
  setPage,
  setBlockSize,
  setMaxRow,
  setUseSortModel,
  setIsDestroyed,
  // 検索条件：月の非活性フラグの設定
  setMonthDisabled,
  // 検索条件：日の非活性フラグの設定
  setDayDisabled,
  // カレンダーデータの設定
  setCalendarData,
  // リマインド明細データの設定
  setReminderListData,
  // カレンダー編集画面のタブの値の設定
  setTabValue,
  // リマインド依頼ダイアログの明細データの設定
  setRowData,
  // リマインド依頼ダイアログの追加ユーザの設定
  setSelectedUser,
  setIsConfirmor,
  // 前回検索条件 アンケートの設定
  setLastSurveys,
  // 前回検索条件：年を設定
  setLastYear,
  // 前回検索条件：月を設定
  setLastMonth,
  // 前回検索条件：日を設定
  setLastDay,
  // 検索条件クリアフラグを設定
  setClearSearchFlag
} = enqueteCalendarSlice.actions;
export default enqueteCalendarSlice.reducer;
